import { baseApi } from '@shared/api/baseApi'
import type {
  BasketItemInput,
  BasketItemResponse,
  BasketResponse,
  CalculatorCardsResponse,
  CalculatorDictionary,
  CalculatorMetadataResponse,
  CalculatorRecipesResponse,
  LabourItem,
  LabourRate,
  LabourRatesResponse,
  RecipeLabourResponse,
  SavedRecipesResponse,
} from '../types'

/** Больше бэк не отдаёт за один запрос — иначе 400. */
export const RECIPES_BATCH_LIMIT = 20

export const fetchCalculatorMetadata =
  async (): Promise<CalculatorMetadataResponse> => {
    const { data } = await baseApi.get<CalculatorMetadataResponse>(
      '/api/calculator/metadata'
    )
    return data
  }

export const fetchCalculatorDictionary =
  async (): Promise<CalculatorDictionary> => {
    const { data } = await baseApi.get<CalculatorDictionary>(
      '/api/calculator/dictionary'
    )
    return data
  }

export const fetchCalculatorCards =
  async (): Promise<CalculatorCardsResponse> => {
    const { data } = await baseApi.get<CalculatorCardsResponse>(
      '/api/calculator/cards'
    )
    return data
  }

/**
 * Составы рецептов. Параметр повторяется: `?card_ids=12&card_ids=48`.
 *
 * Карточки со снятым калькулятором из ответа выпадают — это штатно, поэтому
 * сопоставлять ответ с запросом можно только по `card_id`.
 */
export const fetchCalculatorRecipes = async (
  cardIds: readonly number[]
): Promise<CalculatorRecipesResponse> => {
  if (cardIds.length === 0) return { recipes: [] }
  if (cardIds.length > RECIPES_BATCH_LIMIT) {
    throw new Error(
      `За один запрос можно спросить не больше ${RECIPES_BATCH_LIMIT} рецептов`
    )
  }
  const params = new URLSearchParams()
  cardIds.forEach(id => params.append('card_ids', String(id)))
  const { data } = await baseApi.get<CalculatorRecipesResponse>(
    `/api/calculator/recipes?${params.toString()}`
  )
  return data
}

export const fetchSavedRecipes = async (): Promise<SavedRecipesResponse> => {
  const { data } = await baseApi.get<SavedRecipesResponse>(
    '/api/calculator/saved'
  )
  return data
}

/**
 * Сохранить рецепт. Тела нет, успех — 204, повторное сохранение тоже 204:
 * ручка работает как переключатель, бояться лишнего запроса не нужно.
 * 404 — карточки нет или калькулятор на ней не опубликован.
 */
export const saveRecipe = async (cardId: number): Promise<void> => {
  await baseApi.post(`/api/calculator/saved/${cardId}`)
}

/** Убрать из сохранённых. Если его там и не было — тоже 204. */
export const unsaveRecipe = async (cardId: number): Promise<void> => {
  await baseApi.delete(`/api/calculator/saved/${cardId}`)
}

export const fetchLabourRates = async (): Promise<LabourRatesResponse> => {
  const { data } = await baseApi.get<LabourRatesResponse>(
    '/api/calculator/labour-rates'
  )
  return data
}

/**
 * Сохранить ставки. Присланное заменяет всё: роли, которой нет в запросе, в
 * ставках не останется. Поэтому отправляем форму целиком, а не разницу.
 */
export const saveLabourRates = async (
  rates: readonly Pick<LabourRate, 'role_id' | 'rate'>[]
): Promise<void> => {
  await baseApi.put('/api/calculator/labour-rates', { rates })
}

/** Часы по рецепту. 404 — рецепта нет в сохранённых. */
export const fetchRecipeLabour = async (
  cardId: number
): Promise<RecipeLabourResponse> => {
  const { data } = await baseApi.get<RecipeLabourResponse>(
    `/api/calculator/saved/${cardId}/labour`
  )
  return data
}

/** Сохранить часы. Тоже полная замена: не присланная роль убирается. */
export const saveRecipeLabour = async (
  cardId: number,
  items: readonly LabourItem[]
): Promise<void> => {
  await baseApi.put(`/api/calculator/saved/${cardId}/labour`, { items })
}

export const fetchBasket = async (): Promise<BasketResponse> => {
  const { data } = await baseApi.get<BasketResponse>('/api/calculator/basket')
  return data
}

/**
 * Запись позиции. У ингредиента в корзине одна строка, поэтому повторный POST
 * перезаписывает объём и цену — это же и есть правка.
 */
export const saveBasketItem = async (
  body: BasketItemInput
): Promise<BasketItemResponse> => {
  const { data } = await baseApi.post<BasketItemResponse>(
    '/api/calculator/basket/item',
    body
  )
  return data
}

/** Правка существующей позиции: нет в корзине — 404, молча не создаётся. */
export const updateBasketItem = async (
  body: BasketItemInput
): Promise<BasketItemResponse> => {
  const { data } = await baseApi.put<BasketItemResponse>(
    '/api/calculator/basket/item',
    body
  )
  return data
}

/** Адресуется идентификатором ингредиента, не строки корзины. */
export const deleteBasketItem = async (ingredientId: number): Promise<void> => {
  await baseApi.delete(`/api/calculator/basket/item/${ingredientId}`)
}
